import {
  PolicyField,
  PolicyFieldValue,
  PolicyPrimitive,
  PolicySection,
} from './types';

const humanizeKey = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isNamedField = (item: unknown): item is PolicyField =>
  Boolean(
    item &&
      typeof item === 'object' &&
      'name' in item &&
      'value' in item &&
      typeof (item as { name: unknown }).name === 'string',
  );

function normalizeFieldValue(value: unknown): PolicyFieldValue {
  if (value === undefined) {
    return null;
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value as PolicyPrimitive;
  }

  const mapObjectToFields = (record: Record<string, unknown>): PolicyField[] =>
    Object.entries(record).map(([key, fieldValue]) => ({
      name: humanizeKey(key),
      value: normalizeFieldValue(fieldValue),
    }));

  if (Array.isArray(value)) {
    if (value.every((item) => Array.isArray(item))) {
      return value.map((group) =>
        group.flatMap((item) => {
          if (isNamedField(item)) {
            return [
              { name: item.name, value: normalizeFieldValue(item.value) },
            ];
          }
          if (item && typeof item === 'object') {
            return mapObjectToFields(item as Record<string, unknown>);
          }
          return [{ name: 'Value', value: normalizeFieldValue(item) }];
        }),
      );
    }

    if (value.every((item) => isNamedField(item))) {
      return value.map((item) => ({
        name: item.name,
        value: normalizeFieldValue(item.value),
      }));
    }

    if (value.every((item) => item && typeof item === 'object')) {
      return value.map((item) =>
        mapObjectToFields(item as Record<string, unknown>),
      );
    }

    return value.map((item, index) => {
      if (item && typeof item === 'object') {
        return {
          name: `Item ${index + 1}`,
          value: mapObjectToFields(item as Record<string, unknown>),
        };
      }
      return {
        name: `Item ${index + 1}`,
        value: normalizeFieldValue(item),
      };
    });
  }

  return mapObjectToFields(value as Record<string, unknown>);
}

const getSectionName = (raw: Record<string, unknown>, fallbackName?: string) =>
  typeof raw.name === 'string' ? raw.name : fallbackName || 'Details';

const asPolicyFieldList = (value: PolicyFieldValue): PolicyField[] | null => {
  if (Array.isArray(value) && value.every(isNamedField)) {
    return value as PolicyField[];
  }
  return null;
};

const wrapValueField = (value: PolicyFieldValue): PolicyField[] => [
  { name: 'Value', value },
];

const normalizeSection = (section: unknown, fallbackName?: string) => {
  if (!section || typeof section !== 'object') return null;

  if (isNamedField(section)) {
    const normalizedValue = normalizeFieldValue(section.value);
    const listValue = asPolicyFieldList(normalizedValue);
    return {
      name: section.name || fallbackName || 'Details',
      value: listValue || wrapValueField(normalizedValue),
    };
  }

  const raw = section as Record<string, unknown>;
  const name = getSectionName(raw, fallbackName);
  const normalizedValue = normalizeFieldValue(raw.value ?? raw.data ?? raw);
  const listValue = asPolicyFieldList(normalizedValue);

  if (listValue) {
    return { name, value: listValue };
  }

  return {
    name,
    value: wrapValueField(normalizedValue),
  };
};

export const getPolicySectionsFromResponse = (
  data: unknown,
): PolicySection[] => {
  if (
    Array.isArray(data) &&
    data.every(
      (item) =>
        item &&
        typeof item === 'object' &&
        'recordType' in (item as Record<string, unknown>),
    )
  ) {
    return data
      .map((item) => {
        const record = item as Record<string, unknown>;
        const recordType =
          typeof record.recordType === 'string'
            ? humanizeKey(record.recordType)
            : 'Details';
        return normalizeSection(record.data, recordType);
      })
      .filter(Boolean) as PolicySection[];
  }

  if (Array.isArray(data)) {
    return data
      .map((section) => normalizeSection(section))
      .filter(Boolean) as PolicySection[];
  }

  if (
    data &&
    typeof data === 'object' &&
    'results' in data &&
    Array.isArray((data as { results: unknown }).results)
  ) {
    return (data as { results: unknown[] }).results
      .map((section) => normalizeSection(section))
      .filter(Boolean) as PolicySection[];
  }

  return [];
};

export const isPrimitiveValue = (
  value: PolicyFieldValue,
): value is PolicyPrimitive => !Array.isArray(value);

export const isFieldList = (value: PolicyFieldValue): value is PolicyField[] =>
  Array.isArray(value) && value.every((item) => !Array.isArray(item));

export const isFieldMatrix = (
  value: PolicyFieldValue,
): value is PolicyField[][] =>
  Array.isArray(value) && value.some((item) => Array.isArray(item));

export const formatPrimitiveValue = (value: PolicyPrimitive) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString('en-US')
      : value.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        });
  }

  return String(value);
};

export const getFieldKey = (field: PolicyField, index: number) =>
  `${field.name}-${index.toString()}`;
